/* ============================================================================
   tokenStorage — where the session JWT actually lives.
   ----------------------------------------------------------------------------
   The token is a 30-day bearer credential. It used to sit in localStorage on
   every platform. On the web that is unavoidable (there is nowhere better, and
   the CSP in client/public/_headers is the mitigation there), but on iOS and
   Android localStorage is a plain file inside the app container: it rides along
   in unencrypted device backups and is readable on a jailbroken/rooted device.
   The Keychain / Android Keystore exists for exactly this.

   So: native uses SecureStorage, web keeps localStorage. One module, so no
   caller has to know which.

   WHY THIS IS ASYNC AND localStorage WAS NOT
     Keychain access is asynchronous. That is the whole reason AuthProvider's
     bootstrap had to change — it used to hydrate the token synchronously in a
     useState initializer, which is impossible against the Keychain. See the
     comments there for how the restore path handles it.

   MIGRATION IS SILENT AND ONE-WAY
     Existing installs already have a token in localStorage. loadToken() moves
     it into SecureStorage on first launch after the update and clears the
     localStorage copy. Users stay signed in; nothing prompts them. There is no
     path back — once migrated, native never reads localStorage again except to
     look for a straggler to migrate.

   ON FAILURE, NATIVE DOES NOT FALL BACK TO localStorage
     It would be easy to catch a Keychain error and quietly write the token to
     localStorage instead. That is precisely the bug this module exists to fix,
     and it would be invisible. Instead the session stays in memory for that
     launch and the user signs in again next time. Keychain failure on a healthy
     device is close to unheard of; a silent downgrade to insecure storage is
     not a trade worth making.
   ============================================================================ */
import { Capacitor } from "@capacitor/core";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";

/** Shared across platforms so the migration can find the legacy value. */
const TOKEN_KEY = "nexus_dex_session_token";

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// ── localStorage (web, and the native migration source) ─────────────────────

function readLocal(): string | null {
  try {
    const v = window.localStorage.getItem(TOKEN_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    // Storage blocked (some private/sandboxed contexts). Session lives in
    // memory for this page load; that is the pre-existing web behaviour.
    return null;
  }
}

function writeLocal(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage blocked: tolerate */
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Read the persisted token. On native this also performs the one-time
 * localStorage -> SecureStorage migration.
 *
 * Never throws; a storage failure resolves to null and the user signs in again.
 */
export async function loadToken(): Promise<string | null> {
  if (!isNative()) return readLocal();

  try {
    const v = await SecureStorage.get(TOKEN_KEY);
    // SecureStorage can hold non-string types; only a non-empty string is a
    // usable token.
    if (typeof v === "string" && v.length > 0) return v;
  } catch {
    // Miss or read failure — fall through to the migration check rather than
    // giving up, otherwise an upgrading user is signed out for no reason.
  }

  const legacy = readLocal();
  if (!legacy) return null;

  try {
    await SecureStorage.set(TOKEN_KEY, legacy);
    // Only drop the plaintext copy once the secure write succeeded, so a
    // failure here can never lose someone's session.
    writeLocal(null);
  } catch {
    // Could not migrate. Keep the legacy value so the user stays signed in;
    // we will retry the migration on the next launch.
  }
  return legacy;
}

/**
 * Persist (or clear) the token. Never throws.
 *
 * AuthProvider calls this fire-and-forget from a synchronous setter. A write
 * that loses a race with an immediate app kill costs one re-login, which is
 * why it is not worth making every caller await.
 */
export async function persistToken(token: string | null): Promise<void> {
  if (!isNative()) {
    writeLocal(token);
    return;
  }
  try {
    if (token) await SecureStorage.set(TOKEN_KEY, token);
    else await SecureStorage.remove(TOKEN_KEY);
    // Belt and braces: make sure no plaintext copy survives on native, even if
    // one was written by an older build or a partial migration.
    writeLocal(null);
  } catch {
    // Deliberately NOT falling back to localStorage — see the module docblock.
  }
}

/** True when the token is stored in the platform keystore rather than localStorage. */
export function isSecureTokenStorage(): boolean {
  return isNative();
}
